// src/lib/costFundingService.ts
//
// BekalNanti — P4 Education Cost & Funding Data Service
// Interacts with canonical schema: bekalnanti_app (tables: goal_cost_items, funding_sources)
// RLS and tenant isolation enforced via goal ownership policies.

import { supabase } from './supabase'
import { GoalCostItem, GoalFundingSource } from './costFunding'

/**
 * Map raw database row from bekalnanti_app.goal_cost_items to GoalCostItem.
 */
export function mapRawCostItem(row: any): GoalCostItem {
  return {
    id: row.id,
    goalId: row.goal_id,
    category: row.category,
    name: row.name,
    amount: row.amount !== null && row.amount !== undefined ? Number(row.amount) : null,
    currency: row.currency || 'IDR',
    frequency: row.frequency || 'per_year',
    certainty: row.certainty || 'estimated',
    notes: row.notes || null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

/**
 * Map raw database row from bekalnanti_app.funding_sources to GoalFundingSource.
 */
export function mapRawFundingSource(row: any): GoalFundingSource {
  return {
    id: row.id,
    goalId: row.goal_id,
    sourceType: row.source_type,
    name: row.name,
    amount: row.amount !== null && row.amount !== undefined ? Number(row.amount) : null,
    currency: row.currency || 'IDR',
    frequency: row.frequency || 'one_off',
    status: row.status || 'planned',
    isConfirmed: Boolean(row.is_confirmed),
    sourceReference: row.source_reference || null,
    certainty: row.certainty || 'estimated',
    notes: row.notes || null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

/**
 * Fetch all cost items for a specific goal.
 */
export async function fetchGoalCostItems(goalId: string): Promise<GoalCostItem[]> {
  const { data, error } = await supabase
    .from('goal_cost_items')
    .select('*')
    .eq('goal_id', goalId)
    .order('created_at', { ascending: true })

  if (error) {
    throw new Error(`Gagal memuat komponen biaya: ${error.message}`)
  }

  return (data || []).map(mapRawCostItem)
}

/**
 * Bulk fetch cost items for multiple goals in a household.
 * Returns map of goalId -> GoalCostItem[]
 */
export async function fetchHouseholdGoalCostItems(
  goalIds: string[]
): Promise<Record<string, GoalCostItem[]>> {
  if (goalIds.length === 0) return {}

  const { data, error } = await supabase
    .from('goal_cost_items')
    .select('*')
    .in('goal_id', goalIds)
    .order('created_at', { ascending: true })

  if (error) {
    throw new Error(`Gagal memuat daftar biaya sasaran: ${error.message}`)
  }

  const map: Record<string, GoalCostItem[]> = {}
  for (const gId of goalIds) {
    map[gId] = []
  }
  for (const row of data || []) {
    if (!map[row.goal_id]) map[row.goal_id] = []
    map[row.goal_id]!.push(mapRawCostItem(row))
  }
  return map
}

/**
 * Create a new goal cost item.
 */
export async function createGoalCostItem(
  item: Omit<GoalCostItem, 'id' | 'createdAt' | 'updatedAt'>
): Promise<GoalCostItem> {
  const payload = {
    goal_id: item.goalId,
    category: item.category,
    name: item.name.trim(),
    amount: item.certainty === 'unknown' ? null : item.amount,
    currency: item.currency || 'IDR',
    frequency: item.frequency || 'per_year',
    certainty: item.certainty || 'estimated',
    notes: item.notes?.trim() || null,
  }

  const { data, error } = await supabase
    .from('goal_cost_items')
    .insert(payload)
    .select()
    .single()

  if (error) {
    throw new Error(`Gagal menyimpan komponen biaya: ${error.message}`)
  }

  return mapRawCostItem(data)
}

/**
 * Update an existing goal cost item.
 */
export async function updateGoalCostItem(
  id: string,
  updates: Partial<Omit<GoalCostItem, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<GoalCostItem> {
  const payload: any = {
    updated_at: new Date().toISOString(),
  }

  if (updates.category !== undefined) payload.category = updates.category
  if (updates.name !== undefined) payload.name = updates.name.trim()
  if (updates.amount !== undefined || updates.certainty !== undefined) {
    const cert = updates.certainty
    payload.amount = cert === 'unknown' ? null : updates.amount
  }
  if (updates.currency !== undefined) payload.currency = updates.currency
  if (updates.frequency !== undefined) payload.frequency = updates.frequency
  if (updates.certainty !== undefined) payload.certainty = updates.certainty
  if (updates.notes !== undefined) payload.notes = updates.notes?.trim() || null

  const { data, error } = await supabase
    .from('goal_cost_items')
    .update(payload)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw new Error(`Gagal memperbarui komponen biaya: ${error.message}`)
  }

  return mapRawCostItem(data)
}

/**
 * Delete a goal cost item.
 */
export async function deleteGoalCostItem(id: string): Promise<void> {
  const { error } = await supabase.from('goal_cost_items').delete().eq('id', id)

  if (error) {
    throw new Error(`Gagal menghapus komponen biaya: ${error.message}`)
  }
}

/**
 * Fetch all funding sources for a specific goal.
 */
export async function fetchGoalFundingSources(goalId: string): Promise<GoalFundingSource[]> {
  const { data, error } = await supabase
    .from('funding_sources')
    .select('*')
    .eq('goal_id', goalId)
    .order('created_at', { ascending: true })

  if (error) {
    throw new Error(`Gagal memuat sumber pendanaan: ${error.message}`)
  }

  return (data || []).map(mapRawFundingSource)
}

/**
 * Bulk fetch funding sources for multiple goals in a household.
 * Returns map of goalId -> GoalFundingSource[]
 */
export async function fetchHouseholdGoalFundingSources(
  goalIds: string[]
): Promise<Record<string, GoalFundingSource[]>> {
  if (goalIds.length === 0) return {}

  const { data, error } = await supabase
    .from('funding_sources')
    .select('*')
    .in('goal_id', goalIds)
    .order('created_at', { ascending: true })

  if (error) {
    throw new Error(`Gagal memuat daftar sumber pendanaan: ${error.message}`)
  }

  const map: Record<string, GoalFundingSource[]> = {}
  for (const gId of goalIds) {
    map[gId] = []
  }
  for (const row of data || []) {
    if (!map[row.goal_id]) map[row.goal_id] = []
    map[row.goal_id]!.push(mapRawFundingSource(row))
  }
  return map
}

/**
 * Create a new funding source for a goal.
 */
export async function createGoalFundingSource(
  source: Omit<GoalFundingSource, 'id' | 'createdAt' | 'updatedAt'>
): Promise<GoalFundingSource> {
  const payload = {
    goal_id: source.goalId,
    source_type: source.sourceType,
    name: source.name.trim(),
    amount: source.certainty === 'unknown' ? null : source.amount,
    currency: source.currency || 'IDR',
    frequency: source.frequency || 'one_off',
    status: source.status || 'planned',
    is_confirmed: Boolean(source.isConfirmed),
    source_reference: source.sourceReference || null,
    certainty: source.certainty || 'estimated',
    notes: source.notes?.trim() || null,
  }

  const { data, error } = await supabase
    .from('funding_sources')
    .insert(payload)
    .select()
    .single()

  if (error) {
    throw new Error(`Gagal menyimpan sumber pendanaan: ${error.message}`)
  }

  return mapRawFundingSource(data)
}

/**
 * Update an existing funding source.
 */
export async function updateGoalFundingSource(
  id: string,
  updates: Partial<Omit<GoalFundingSource, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<GoalFundingSource> {
  const payload: any = {
    updated_at: new Date().toISOString(),
  }

  if (updates.sourceType !== undefined) payload.source_type = updates.sourceType
  if (updates.name !== undefined) payload.name = updates.name.trim()
  if (updates.amount !== undefined || updates.certainty !== undefined) {
    const cert = updates.certainty
    payload.amount = cert === 'unknown' ? null : updates.amount
  }
  if (updates.currency !== undefined) payload.currency = updates.currency
  if (updates.frequency !== undefined) payload.frequency = updates.frequency
  if (updates.status !== undefined) payload.status = updates.status
  if (updates.isConfirmed !== undefined) payload.is_confirmed = updates.isConfirmed
  if (updates.sourceReference !== undefined) payload.source_reference = updates.sourceReference
  if (updates.certainty !== undefined) payload.certainty = updates.certainty
  if (updates.notes !== undefined) payload.notes = updates.notes?.trim() || null

  const { data, error } = await supabase
    .from('funding_sources')
    .update(payload)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw new Error(`Gagal memperbarui sumber pendanaan: ${error.message}`)
  }

  return mapRawFundingSource(data)
}

/**
 * Delete a funding source.
 */
export async function deleteGoalFundingSource(id: string): Promise<void> {
  const { error } = await supabase.from('funding_sources').delete().eq('id', id)

  if (error) {
    throw new Error(`Gagal menghapus sumber pendanaan: ${error.message}`)
  }
}
